import { useCallback, useEffect, useMemo, useState } from "react";

import { useCollectionGroup, useCollectionGroupReference } from "../../firebase";
import { getCountFromServer } from "firebase/firestore";

/**
 * Returns a list containing the IDs of emails that have any
 * in-progress email sending tasks.
 */
export function useConcurrentEmails() {
  const [tasks, loading, error] = useCollectionGroup("tasks", [
    { op: "where", args: ["type", "==", "send-emails"] },
    { op: "where", args: ["status", "==", "in-progress"] },
  ]);

  const sends = useMemo(() => {
    if (!tasks) return undefined;
    const emailIds = tasks.docs.map((task) => {
      const [, emailId] = task.ref.path.split("/");
      return emailId;
    });
    return Array.from(new Set(emailIds));
  }, [tasks]);

  return [sends, loading, error] as const;
}

/**
 * Returns the number of in-progress email send tasks.
 *
 * NOTE: this is not a realtime query. Use the returned
 * `refetch` function to refresh the value.
 *
 * @returns `[count, loading, refetch]`
 */
export function useConcurrentTaskCount() {
  const ref = useCollectionGroupReference("tasks", [
    { op: "where", args: ["type", "==", "send-emails"] },
    { op: "where", args: ["status", "==", "in-progress"] },
  ]);

  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState<number>();

  const getCount = useCallback(async () => {
    if (!ref) return undefined;
    setLoading(true);
    const snapshot = await getCountFromServer(ref);
    setCount(snapshot.data().count);
    setLoading(false);
  }, [ref]);

  useEffect(() => {
    getCount();
  }, [getCount]);

  return [count, loading, getCount] as const;
}
